/**
 * Frame mount layout + weapon size-fit rules, ported from COMP/CON
 * (`reference/compcon` `Mount.generateSlots` + `Rules.MountFittings`, which reads
 * `@massif/lancer-data`'s `rules.mount_fittings`). These are the authoritative
 * tables the reducer size-gates against; kept here as plain data so the reducer
 * stays a switch over pure helpers.
 *
 * Two tables:
 *   - {@link MOUNT_SLOT_SIZES}: a frame mount-type string → the ordered fitting
 *     sizes of the slots it provides. (`select_frame` materializes `MountData`
 *     from this.)
 *   - {@link SLOT_FITTINGS}: a slot fitting size → the weapon sizes it accepts.
 *     (`equip_weapon` size-gates against this.)
 *
 * Frame mount strings seen in core data: Main, Heavy, Flex, Main/Aux, Aux/Aux.
 * Weapon size strings: Auxiliary, Main, Heavy, Superheavy, Ship-class.
 */

/**
 * A frame mount type → the fitting size of each slot it generates, in order.
 * Matches COMP/CON `Mount.generateSlots`.
 *
 * **Flex is a static two-slot mount, `["Flex", "Auxiliary"]`** (core p.32:
 * "Flexible mounts can take either one MAIN weapon or up to two AUXILIARY
 * weapons") — slot 0 keeps the Flex fitting (accepts Main or Auxiliary), slot 1
 * is a plain Auxiliary fitting. This table alone cannot express the printed
 * "either/or": `["Flex","Auxiliary"]` on its own would also admit (Main, Aux)
 * together, which RAW forbids. That cross-slot exclusivity is enforced
 * separately, as the `"flex"` tenant in `checkLoadoutInvariants`
 * (`loadoutInvariants.ts`) — a Main-sized weapon in slot 0 hard-rejects an equip
 * into slot 1, and vice versa, naming the fix rather than silently dropping the
 * player's weapon. A static two-slot table plus a wall tenant, deliberately not
 * COMP/CON's dynamic mutate-on-read `Slots` getter, which silently unequips the
 * player's second Aux as a side effect of a read.
 */
export const MOUNT_SLOT_SIZES: Record<string, string[]> = {
  Main: ["Main"],
  Heavy: ["Heavy"],
  Aux: ["Auxiliary"],
  "Aux/Aux": ["Auxiliary", "Auxiliary"],
  "Main/Aux": ["Main", "Auxiliary"],
  Flex: ["Flex", "Auxiliary"],
};

/**
 * A slot's fitting size → the weapon sizes it accepts. Mirrors
 * `@massif/lancer-data`'s `rules.mount_fittings`. A Heavy slot accepts anything up
 * to Superheavy (the `Superheavy` entry here is a WEAPON size, not a mount type —
 * see the note below on the mount type that shares the name).
 */
export const SLOT_FITTINGS: Record<string, string[]> = {
  Auxiliary: ["Auxiliary"],
  Main: ["Main", "Auxiliary"],
  Flex: ["Main", "Auxiliary"],
  Heavy: ["Superheavy", "Heavy", "Main", "Auxiliary"],
};

/**
 * **No `Superheavy` or `Integrated` mount type** — neither is reachable by any frame's
 * data. `Superheavy` (as a mount TYPE, distinct from the `Superheavy` WEAPON size still
 * admitted above) is COMP/CON's synthetic mount for the `cb_superheavy_mounting` core
 * bonus, which exists in neither our `lancer-data` snapshot's core bonuses nor the core
 * rulebook — no frame ever carries the string `"Superheavy"` as a mount type (the only
 * values across the 29 core frames are `Flex`, `Main`, `Heavy`, `Main/Aux`, `Aux/Aux`).
 * `Integrated` is doubly unreachable: RAW's integrated mounts are already modelled
 * *outside* this table — a frame's `core_system.integrated` → `spawn.ts`'s
 * `integratedIds` → SP-exempt `integrated:${i}` instance ids, with `equip_weapon`
 * hard-rejecting a manual integrated-weapon equip separately (`reducer.ts`). Reinstating
 * either mount type is that content's job, not a gap here — `mounts.raw.pin.test.ts` is
 * the pin that reddens on a conscious re-addition.
 */

/** The slot fitting sizes for a frame mount-type string, or `undefined` if unknown. */
export function slotSizesForMount(mountType: string): string[] | undefined {
  return MOUNT_SLOT_SIZES[mountType];
}

/**
 * Whether a weapon of `weaponSize` fits a slot of `slotSize`. An unknown slot
 * size (a mount string we don't model) admits nothing (caller rejects). A weapon
 * with no declared size is treated as not fitting any sized slot.
 */
export function weaponFitsSlot(
  slotSize: string,
  weaponSize: string | undefined,
): boolean {
  const accepted = SLOT_FITTINGS[slotSize];
  if (!accepted) return false;
  if (weaponSize === undefined) return false;
  return accepted.includes(weaponSize);
}

/**
 * Whether a weapon **mod** fits a host weapon, by weapon *type* — the mod analogue
 * of {@link weaponFitsSlot}. A mod declares `allowed_types` (a list of weapon types
 * like "Rifle" | "Melee" | "Cannon", from `@massif/lancer-data`); the host weapon
 * carries a `type`. The mod fits iff the host's `type` is in `allowed_types`.
 *
 * Mirrors COMP/CON `WeaponMod.AllowedTypes` gating. Two permissive cases match the
 * data shape: an **absent/empty `allowed_types`** means the mod fits *any* type
 * (an unrestricted mod), and otherwise an unmatched host `type` (including a weapon
 * with no declared `type`) does not fit. Pure (no compendium) — the caller resolves
 * the two values off the compendium and passes them in, like `weaponFitsSlot`.
 */
export function modFitsWeapon(
  weaponType: string | undefined,
  allowedTypes: readonly string[] | undefined,
): boolean {
  if (!allowedTypes || allowedTypes.length === 0) return true;
  if (weaponType === undefined) return false;
  return allowedTypes.includes(weaponType);
}
